import { useState } from 'react';
import {
  Box, Button, Center, FormControl, FormErrorMessage, IconButton, Image, Input, InputGroup,
  InputRightElement, Link, Text, VStack,
} from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import * as AuthService from 'services/AuthService';
import { LoginTokens } from 'services/AuthService';
import logo from 'utils/assets/img/logo.png';

interface LoginPageProps {
  onLoginSuccess: (tokens: LoginTokens) => void;
}

interface PasswordInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}

function PasswordInput({ label, value, onChange, autoFocus }: PasswordInputProps) {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <FormControl>
      <Text fontWeight="bold" mb={1}>{label}</Text>
      <InputGroup>
        <Input
          type={isVisible ? 'text' : 'password'}
          placeholder={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          focusBorderColor="gray.500"
          _hover={{ borderColor: 'gray.500' }}
          autoFocus={autoFocus}
        />
        <InputRightElement>
          <IconButton
            type="button"
            aria-label={isVisible ? 'Nascondi password' : 'Mostra password'}
            icon={isVisible ? <MdVisibilityOff /> : <MdVisibility />}
            onClick={() => setIsVisible((v) => !v)}
            variant="ghost"
            size="sm"
            tabIndex={-1}
            _focus={{ boxShadow: 'none' }}
          />
        </InputRightElement>
      </InputGroup>
    </FormControl>
  );
}

export default function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const [mode, setMode] = useState<'login' | 'change-password'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const backToLogin = () => {
    setMode('login');
    setNewPassword('');
    setConfirmPassword('');
    setErrorMessage('');
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!username.trim()) {
      setErrorMessage('Inserisci email o username');
      return;
    }
    if (!password) {
      setErrorMessage('Inserisci la password');
      return;
    }

    setIsSubmitting(true);
    const result = await AuthService.login(username.trim(), password);
    setIsSubmitting(false);

    switch (result.status) {
      case 'success':
        onLoginSuccess(result.tokens);
        break;
      case 'password_change_required':
        setMode('change-password');
        break;
      case 'invalid_credentials':
        setErrorMessage('Email/username o password non corretti');
        break;
      default:
        setErrorMessage('Errore di connessione. Riprova.');
    }
  };

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (newPassword.length < 8) {
      setErrorMessage('La nuova password deve avere almeno 8 caratteri');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setErrorMessage('La nuova password deve contenere almeno una lettera maiuscola e un numero');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage('Le password non coincidono');
      return;
    }

    setIsSubmitting(true);
    const result = await AuthService.changePassword(username.trim(), password, newPassword);
    setIsSubmitting(false);

    switch (result.status) {
      case 'success':
        onLoginSuccess(result.tokens);
        break;
      case 'invalid_credentials':
        setErrorMessage('Sessione non valida, effettua di nuovo il login');
        backToLogin();
        break;
      default:
        setErrorMessage('Errore durante il cambio password. Riprova.');
    }
  };

  return (
    <Center minH="100vh" backgroundColor="#f0f4f8">
      <Box
        as="form"
        onSubmit={mode === 'login' ? handleLoginSubmit : handleChangePasswordSubmit}
        backgroundColor="white"
        borderRadius="20px"
        boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.18)"
        p={{ base: '30px', md: '40px' }}
        w={{ base: '90%', sm: '380px' }}
      >
        <VStack spacing={5} align="stretch">
          <Center>
            <Image src={logo} alt="EGR SRL" maxH="50px" />
          </Center>

          {mode === 'login' ? (
            <>
              <Text fontSize="2xl" fontWeight="bold" color="#9FB50C" textAlign="center">
                Accedi
              </Text>

              <FormControl isInvalid={!!errorMessage}>
                <Text fontWeight="bold" mb={1}>Email o Username</Text>
                <Input
                  type="text"
                  placeholder="nome@ecogrouprizzi.it"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  focusBorderColor="gray.500"
                  _hover={{ borderColor: 'gray.500' }}
                  autoFocus
                />
              </FormControl>

              <FormControl isInvalid={!!errorMessage}>
                <PasswordInput label="Password" value={password} onChange={setPassword} />
                {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
              </FormControl>
            </>
          ) : (
            <>
              <Text fontSize="2xl" fontWeight="bold" color="#9FB50C" textAlign="center">
                Imposta una nuova password
              </Text>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                La tua password è temporanea: per continuare devi impostarne una nuova.
              </Text>

              <Box>
                <PasswordInput label="Nuova password" value={newPassword} onChange={setNewPassword} autoFocus />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Almeno 8 caratteri, con una lettera maiuscola e un numero.
                </Text>
              </Box>

              <FormControl isInvalid={!!errorMessage}>
                <PasswordInput label="Conferma nuova password" value={confirmPassword} onChange={setConfirmPassword} />
                {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
              </FormControl>
            </>
          )}

          <Button
            type="submit"
            isLoading={isSubmitting}
            _hover={{ bg: 'gray.700' }}
            _active={{ bg: 'gray.600' }}
            _focus={{ boxShadow: 'none' }}
            backgroundColor="#688CC8"
            color="white"
            height="42px"
            borderRadius="6px"
          >
            {mode === 'login' ? 'Accedi' : 'Salva nuova password'}
          </Button>

          {mode === 'change-password' && (
            <Link fontSize="sm" color="gray.500" textAlign="center" onClick={backToLogin}>
              Torna al login
            </Link>
          )}
        </VStack>
      </Box>
    </Center>
  );
}
